import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { EjournalApiService } from '@et/api';
import { EjournalService } from '../../services/ejournal.service';
import {
  AddEtJournalSignerDto,
  EtJournalSigner,
  EtJournalSignerWithId,
  PatchEtJournalSignerDto,
} from '@et/typings';
import { SignerFormComponent } from '../signer-form/signer-form.component';
import { Packet } from '@escrowtab/packets-api-client';
import { catchError, finalize, map, Observable } from 'rxjs';

@Component({
  selector: 'et-journal-journal-signer-add-edit',
  imports: [SignerFormComponent, MatDialogClose],
  templateUrl: './journal-signer-add-edit.component.html',
  styleUrl: './journal-signer-add-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalSignerAddEditComponent {
  private readonly dialogRef =
    inject<MatDialogRef<JournalSignerAddEditComponent>>(MatDialogRef);
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly ejournalService = inject(EjournalService);
  readonly dialogData = inject<{
    signer?: EtJournalSigner;
  }>(MAT_DIALOG_DATA);

  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  get isEditMode(): boolean {
    return !!this.dialogData?.signer;
  }

  get packet(): Packet {
    return this.ejournalService.selectedPacket()!;
  }

  get signer(): EtJournalSigner | null {
    return this.dialogData?.signer ? this.dialogData.signer : null;
  }

  get ipAddress(): string {
    return this.ejournalService.ip() ?? '';
  }

  onSubmint(data: Partial<AddEtJournalSignerDto | PatchEtJournalSignerDto>) {
    // Make sure to include the ejournalId and packetId in the payload
    const payload = {
      ...data,
      ejournalId: this.ejournalService.eJournal()!.id,
      packetId: this.packet.packetId,
    };

    this.isLoading.set(true);
    this.error.set(null);
    this.dialogRef && (this.dialogRef.disableClose = true);

    const op$: Observable<EtJournalSignerWithId> = this.isEditMode
      ? this.ejournalApiService.patchSigner(
          this.signer!.eJournalSignerId,
          payload,
        )
      : this.ejournalApiService.addSigner(payload as AddEtJournalSignerDto);

    op$
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.dialogRef && (this.dialogRef.disableClose = false);
        }),
        map((signer) => {
          const eJournalSignerId = signer.id;
          const { id, ...signerWithoutId } = signer;
          return {
            ...signerWithoutId,
            eJournalSignerId,
            signatures: this.signer?.signatures ?? [],
          };
        }),
        catchError((error) => {
          this.error.set(
            this.isEditMode
              ? 'Failed to update signer'
              : 'Failed to add signer',
          );
          throw error;
        }),
      )
      .subscribe((signer) => {
        this.ejournalService.updateSigners((signers) => {
          const signersCopy = [...signers];
          const index = signersCopy.findIndex(
            (s) => s.eJournalSignerId === signer.eJournalSignerId,
          );
          if (index !== -1) {
            signersCopy[index] = signer;
          } else {
            signersCopy.push(signer);
          }
          return signersCopy;
        });
        this.dialogRef?.close();
      });
  }
}
